import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API, handleUnauthorized } from "@/lib/api";

const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Global axios 401 handler: expired token -> auto logout + redirect to login.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleUnauthorized(error.config?.url || '');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Apply token to axios defaults immediately so any in-flight request
      // (payment, invoice fetch, etc.) is authenticated even before /auth/me resolves.
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token and get user info
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        setUser(response.data);
      })
      .catch((error) => {
        console.log('Auth token verification failed:', error.response?.status);
        // Clear invalid token
        localStorage.removeItem('auth_token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      })
      .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password, garageCode) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
        garage_code: garageCode || undefined,
      });
      
      const { user, token } = response.data;
      localStorage.setItem('auth_token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      return { success: true, message: 'Login successful' };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore logout errors
      console.log('Logout API error (ignoring):', error.message);
    } finally {
      // Always clear local auth state
      localStorage.removeItem('auth_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  // Helper function to clear auth state manually
  const clearAuthState = () => {
    localStorage.removeItem('auth_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    window.location.reload(); // Force page refresh
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      return { success: true, user: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const hasPermission = (requiredRole) => {
    if (!user) return false;
    
    const roleHierarchy = {
      'super_admin': 3,
      'management': 2,
      'normal_user': 1,
      'basic_user': 1
    };

    const userLevel = roleHierarchy[user.role];
    if (userLevel === undefined) return false;

    // Support both a single role string and an array of allowed roles.
    // For an array, grant access if the user meets the lowest role's level in the list.
    if (Array.isArray(requiredRole)) {
      const minRequiredLevel = Math.min(...requiredRole.map((r) => roleHierarchy[r] ?? Infinity));
      return userLevel >= minRequiredLevel;
    }

    return userLevel >= roleHierarchy[requiredRole];
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      clearAuthState,
      register,
      hasPermission,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, useAuth, AuthProvider };
